import type { ServiceItem } from '@/cms/fields/types/checkout-page'
import { Banner, type BannerColors } from '@sunwebgroup/uds-ui/banner'
import { InputStepper } from '@sunwebgroup/uds-ui/input-stepper'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { getTotalPrice } from '@/scopes/checkout/components/services/DinnerVouchersService/selectors'
import { updateDinnerVouchersQuantity } from '@/scopes/checkout/domain/actions'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { BannerType } from '@/scopes/common/domain/types/banner'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createGenericServiceEvent } from '../../../domain/analytics'
import {
  bannerColorMap,
  type CheckoutServiceBanner,
  ServiceType,
} from '../../../domain/types'
import styles from './DinnerVouchersService.module.css'

const maxVoucherQuantity = 25

export function DinnerVouchersService({ item }: { item: ServiceItem }) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { service, setServiceValue, setServiceLocalValue } = useService(ServiceType.DinnerVouchers)
  const setServiceValueTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const { componentTitle, componentDescription, popupTitle, popupDescription, popupReadMoreLabel } = item

  useSendAncillaryAvailable(service, 'select MEAL-VOUCHERS')

  const handleQuantityChange = React.useCallback(
    (quantity: number) => {
      if (!service)
        return
      const optionValue = service.selectedValue ?? service.options[0]?.value ?? null
      const updatedService = updateDinnerVouchersQuantity({ quantity, value: optionValue })(service)

      if (quantity > 0 && optionValue === null)
        return

      setServiceLocalValue(() => updatedService)

      if (setServiceValueTimerRef.current !== null)
        clearTimeout(setServiceValueTimerRef.current)
      setServiceValueTimerRef.current = setTimeout(() => {
        setServiceValue(updatedService)
        setServiceValueTimerRef.current = null
      }, 2000)

      sendEvent(
        createGenericServiceEvent({
          description: 'select MEAL-VOUCHERS',
          element: 'input stepper',
          type: 'select MEAL-VOUCHERS',
          result: String(quantity),
        }),
      )
    },
    [service, setServiceValue, setServiceLocalValue, sendEvent],
  )

  if (!service)
    return null

  const { options, selectedQuantity, banners } = service

  const option = options.find(({ value }) => value === service.selectedValue) ?? options[0]
  if (!option)
    return null
  const totalPrice = getTotalPrice(service)

  return (
    <CheckoutSection
      title={componentTitle || intl.formatMessage({ id: 'checkout_steps_ancillaries_dinner_vouchers_title' })}
      description={componentDescription}
      glyph={getIconForService(ServiceType.DinnerVouchers)}
      moreInformationTitle={popupTitle}
      moreInformationDescription={popupDescription}
      moreInformationReadMoreLabel={popupReadMoreLabel}
    >
      <div className={styles.content}>
        <ListItem variant="outlined">
          <div className={styles.optionRow}>
            <div className={styles.optionInfo}>
              <Typography variant="body-m-regular-tight">
                {option.label}
              </Typography>
              <Typography variant="body-s-regular-tight" className={styles.unitPrice}>
                {intl.formatCurrency(option.price)}
                {' '}
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_dinner_vouchers_per_unit_label' })}
              </Typography>
            </div>
            <InputStepper
              minimumValue={0}
              maximumValue={maxVoucherQuantity}
              initialValue={selectedQuantity}
              size="M"
              id="dinner-vouchers-quantity"
              onChange={handleQuantityChange}
              decrementButtonAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_dinner_vouchers_quantity_decrement_aria_label' })}
              incrementButtonAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_dinner_vouchers_quantity_increment_aria_label' })}
              valueAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_dinner_vouchers_quantity_value_aria_label' })}
            />
          </div>
        </ListItem>

        {selectedQuantity > 0 && (
          <ListItem variant="tinted">
            <div className={styles.totalRow}>
              <div className={styles.totalInfo}>
                <Typography variant="body-l-bold-tight">
                  {intl.formatMessage({ id: 'checkout_steps_ancillaries_dinner_vouchers_total_price_label' })}
                </Typography>
                <Typography variant="body-s-regular-tight" className={styles.totalHint}>
                  {intl.formatMessage(
                    { id: 'checkout_steps_ancillaries_dinner_vouchers_total_price_hint' },
                    { quantity: selectedQuantity },
                  )}
                </Typography>
              </div>
              <Typography variant="body-l-bold-tight">
                {intl.formatCurrency(totalPrice)}
              </Typography>
            </div>
          </ListItem>
        )}

        {banners.length > 0 && <DinnerVouchersBanners banners={banners} />}
      </div>
    </CheckoutSection>
  )
}

function DinnerVouchersBanners({ banners }: { banners: CheckoutServiceBanner[] }) {
  return (
    <>
      {banners.map(({ title, text, type = 'info' }, index) => (
        <Banner
          key={`banner-${index}`}
          color={bannerColorMap[type as BannerType] as BannerColors}
          size="M"
        >
          <Banner.TextsWrapper>
            {title && <Banner.Text>{title}</Banner.Text>}
            {text && <Banner.SupportingText innerHTML={text} />}
          </Banner.TextsWrapper>
        </Banner>
      ))}
    </>
  )
}
